import { Canvas, useThree, events as createPointerEvents, type Events } from '@react-three/fiber';
import { Grid, OrbitControls } from '@react-three/drei';
import { ReactNode, Suspense, useCallback, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Box, Spinner, Center } from '@chakra-ui/react';
import { useAppStore } from '../state/store';
import { useBIMStore } from '../state/useBIMStore';
import { useRenderSettingsStore } from '../state/useRenderSettingsStore';
import { useContextMenuStore } from '../state/useContextMenuStore';
import { useSystemVarsStore } from '../state/useSystemVarsStore';
import { useViewportColorsStore } from '../state/useViewportColorsStore';
import { useIsDarkMode } from '../hooks/useThemeInk';
import { collectDocumentBounds } from './documentBounds';
import { shouldRightClickComplete, finishActiveCommand, tryRepeatLastCommand } from '../utils/commandCompletion';
import { PostProcessing } from './AdvancedRendering';
import { DemandRenderController } from './RenderLoopController';
import { ProceduralEnvironment } from './ProceduralEnvironment';
import { WorkbenchViewCube } from '../components/viewport/WorkbenchViewCube';
import { ViewCubeHud } from './ViewCubeHud';
import { registerViewportCapture, unregisterViewportCapture } from '../services/mcp/viewportCapture';
import { setActiveEngineCamera } from './engineCameraRegistry';
import { notifyCameraChanged } from './cameraTick';
import '../styles/global.css';

/**
 * Event manager that keeps DOM overlays out of the canvas raycast pipeline.
 *
 * Overlays portalled inside the R3F container (drei <Html> snap labels, BIM
 * tags, in-canvas text editors) bubble their pointer events up to R3F's
 * event source. R3F's default compute reads offsetX/offsetY, which are
 * relative to the EVENT TARGET — for an overlay that maps the cursor's
 * position inside a small label onto the full canvas, so the raycast lands
 * near the viewport's top-left corner: the active command's rubber-band
 * jumps there and commits there if the command finishes while the cursor
 * sits on the overlay. Overlays own their input — events whose target is
 * not the canvas are dropped before R3F sees them.
 */
type ViewportStore = Parameters<typeof createPointerEvents>[0];

function overlaySafeEvents(store: ViewportStore): ReturnType<typeof createPointerEvents> {
    const events = createPointerEvents(store);
    const handlers = Object.fromEntries(
        Object.entries(events.handlers ?? {}).map(([name, handler]) => [
            name,
            (event: Event) => {
                const gl = store.getState().gl;
                if (!gl || event.target !== gl.domElement) return;
                handler(event);
            },
        ])
    ) as Events;
    return { ...events, handlers };
}

/**
 * EngineViewport — the single shared 3D engine shell (Phase A3).
 *
 * Every workbench (CAD/3D via `rendering/Viewport`, BIM via
 * `views/BimViewport`) renders through this component. It owns the parts
 * that must never diverge:
 *   - the R3F Canvas (gl config, dpr, demand render loop)
 *   - camera mount + OrbitControls defaults (Alt+middle orbit handling)
 *   - the procedural PBR environment
 *   - the ViewCube HUD (position/toggle from app store)
 *
 * Everything workbench-specific arrives as an inert scene-graph slot
 * (`lighting`, `grid`, `axes`, `environmentOverlays`, `children`) or stays
 * in the shell that composes this engine. No `if (mode)` branching is ever
 * added here — behaviour differences are expressed through props/slots by
 * the workbench shells.
 */

export interface EngineControlsConfig {
    /** React key that forces a controls remount when the pose class changes. */
    controlsKey?: string | number;
    target?: [number, number, number];
    enableRotate?: boolean;
    enableZoom?: boolean;
    enablePan?: boolean;
    minPolarAngle?: number;
    maxPolarAngle?: number;
    minDistance?: number;
    maxDistance?: number;
    screenSpacePanning?: boolean;
    mouseButtons?: Partial<{
        LEFT: THREE.MOUSE;
        MIDDLE: THREE.MOUSE;
        RIGHT: THREE.MOUSE;
    }>;
}

export interface EngineViewportProps {
    /** Camera element (Perspective/Orthographic) — the shell owns its pose. */
    camera: ReactNode;
    /** Scene content of the workbench (scene provider tree). */
    children: ReactNode;
    /** Slot rendered before the environment (light rigs). */
    lighting?: ReactNode;
    /** Slot rendered after the environment (overlay scene graphs). */
    environmentOverlays?: ReactNode;
    /** drei Grid element from the shell, or null to omit. */
    grid?: ReactNode;
    /** Axes helper element from the shell, or null to omit. */
    axes?: ReactNode;
    /** Enable the settings-driven EffectComposer pass (3D workbench only). */
    postProcessing?: boolean;
    /** Canvas shadows flag (shells decide; default true). */
    shadows?: boolean;
    /** OrbitControls configuration for this workbench. */
    controls?: EngineControlsConfig;
    /** Native context-menu handler on the container (selection-aware menus). */
    onContextMenu?: (e: React.MouseEvent) => void;
    /** Optional wrapper class (e.g. 'bim-viewport' theme background). */
    containerClassName?: string;
    /** Chakra background token; null delegates background to CSS class. */
    containerBg?: string | null;
    /** Optional canvas class hook for CSS sizing/backgrounds. */
    canvasClassName?: string;
    /** When set, wraps the Canvas in a Suspense with this fallback. */
    suspenseFallback?: ReactNode;
    /** DOM slot rendered after the canvas inside the container (bars, panels). */
    overlay?: ReactNode;
    /** Show the 3D ViewCube HUD (2D workbenches set this to false). */
    showViewCube?: boolean;
    /** Mount the procedural PBR environment (off for lightweight 2D shells). */
    environment?: boolean;
    /** Respond to the shared 'yap:set-view' orientation commands. */
    viewCommands?: boolean;
    /**
     * Middle-button semantics. 'pan' (default) keeps the shared 2D-style
     * MBUTTONPAN model; 'orbit' (Sketch workbench) makes the middle button
     * always orbit and shifts panning onto Shift+Middle, matching Sketch's
     * navigation language.
     */
    middleButtonMode?: 'pan' | 'orbit';
}

/** Shared WebGL config (identical across all workbenches). */
const ENGINE_GL_CONFIG = {
    antialias: true,
    toneMapping: THREE.ACESFilmicToneMapping,
    toneMappingExposure: 1.0,
    // No `preserveDrawingBuffer`: MCP capture renders synchronously via the
    // registered renderNow hook and reads the buffer inside the same rAF task
    // (see services/mcp/viewportCapture.ts). The flag costs a full back-buffer
    // blit on every presented frame for zero visual benefit.
} as const;

/**
 * Repaint-on-theme-flip mount (demand-loop parity with useIsDarkMode
 * consumers): a color-mode switch writes no zustand store, so without a
 * subscriber the canvas keeps presenting the previous mode's frame while the
 * CSS behind it has already flipped — stale dark grid on the light canvas.
 * Mounted in every EngineViewport canvas so the flip repaints them all.
 */
function ThemeRepaintMount() {
    useIsDarkMode();
    return null;
}

/**
 * Repaint-on-color-edit mount: the Settings › Color editor rewrites the
 * inline :root viewport tokens; grid tones are scene-graph props, so the
 * demand loop needs an explicit frame after each edit (CSS backgrounds
 * repaint themselves — only the drawn grid needs this).
 */
function ViewportColorsRepaintMount() {
    const invalidate = useThree((s) => s.invalidate);
    const revision = useViewportColorsStore((s) => s.revision);
    useEffect(() => { invalidate(); }, [revision, invalidate]);
    return null;
}

/**
 * Registers the live canvas + invalidate fn + a synchronous single-frame
 * `renderNow` hook with the MCP viewport capture bridge so yap_screenshot can
 * grab the frame. Mounted inside <Canvas>.
 */
function ViewportCaptureMount() {
    const gl = useThree((s) => s.gl);
    const invalidate = useThree((s) => s.invalidate);
    const advance = useThree((s) => s.advance);
    useEffect(() => {
        const canvas = gl.domElement;
        registerViewportCapture(canvas, invalidate, () => advance(performance.now()));
        return () => unregisterViewportCapture(canvas);
    }, [gl, invalidate, advance]);
    return null;
}

/**
 * Publishes the engine's live camera to the DOM-overlay registry so corner
 * overlays (viewport axes gizmo) can mirror the view orientation without
 * living inside the scene graph.
 */
function EngineCameraRegistryMount() {
    const camera = useThree((s) => s.camera);
    useEffect(() => {
        setActiveEngineCamera(camera);
        return () => setActiveEngineCamera(null);
    }, [camera]);
    return null;
}

/**
 * ViewCube HUD container — full-takeover priority-1 pass that renders the
 * main scene and then the cube layer deterministically (drei issue #519).
 */
function ViewCubeContainer({ enabled = true }: { enabled?: boolean }) {
    const camera = useThree((state) => state.camera);
    const controls = useThree((state) => state.controls);
    const position = useAppStore((state) => state.viewCubeOptions.position);
    const showViewCube = useAppStore((state) => state.viewCubeOptions.showViewCube);

    if (!enabled || !showViewCube) return null;

    const alignment = position === 'Top Right'
        ? 'top-right'
        : position === 'Top Left'
            ? 'top-left'
            : position === 'Bottom Left'
                ? 'bottom-left'
                : 'bottom-right';

    return (
        <ViewCubeHud alignment={alignment} margin={[80, 80]}>
            <WorkbenchViewCube mainCamera={camera} mainControls={controls} />
        </ViewCubeHud>
    );
}

/**
 * Standard orientation handler: responds to the shared 'yap:set-view' event
 * (canonical commands Top/Front/Right/Perspective) by posing the active
 * camera + orbit controls against the Z-up world frame. Mounted inside
 * every EngineViewport canvas, so the command behaves identically in all
 * workbenches.
 */
function ViewCommandHandler() {
    const camera = useThree((s) => s.camera);
    const controls = useThree((s) => s.controls) as
        | { target: THREE.Vector3; update: () => void }
        | null;
    const invalidate = useThree((s) => s.invalidate);

    useEffect(() => {
        const handleSetView = (e: Event) => {
            const view = (e as CustomEvent<{ view: string }>).detail?.view;
            if (!view) return;
            // Z-up world: directions toward the viewer for each preset.
            const dir = new THREE.Vector3(0, 0, 1);
            if (view === 'front') dir.set(-1, 0, 0);
            else if (view === 'right') dir.set(0, 1, 0);
            else if (view === 'perspective') dir.set(1, -1, 1);
            dir.normalize();
            const center = controls ? controls.target.clone() : new THREE.Vector3();
            const radius = Math.max(controls ? camera.position.distanceTo(center) : 30, 1);
            camera.up.set(0, 0, 1);
            camera.position.copy(center).addScaledVector(dir, radius);
            camera.lookAt(center);
            camera.updateProjectionMatrix();
            if (controls) {
                controls.target.copy(center);
                controls.update();
            }
            invalidate();
            notifyCameraChanged();
        };
        // Turntable presentation spin.
        let raf = 0;
        let spinOn = false;
        // Scratch vectors reused every spin frame (was fresh clones + a fresh
        // axis vector per requestAnimationFrame — steady GC churn while spinning).
        const spinCenter = new THREE.Vector3();
        const spinOffset = new THREE.Vector3();
        const spinAxis = new THREE.Vector3(0, 0, 1);
        const spin = () => {
            if (!spinOn) return;
            const center = controls ? spinCenter.copy(controls.target) : spinCenter.set(0, 0, 0);
            const offset = spinOffset.copy(camera.position).sub(center);
            offset.applyAxisAngle(spinAxis, 0.004);
            camera.position.copy(center).add(offset);
            camera.lookAt(center);
            if (controls) controls.update();
            invalidate();
            notifyCameraChanged();
            raf = requestAnimationFrame(spin);
        };
        const handleTurntable = (e: Event) => {
            spinOn = (e as CustomEvent<{ on: boolean }>).detail?.on ?? false;
            if (spinOn) raf = requestAnimationFrame(spin);
            else cancelAnimationFrame(raf);
        };
        window.addEventListener('yap:set-view', handleSetView);
        window.addEventListener('yap:turntable', handleTurntable);
        return () => {
            window.removeEventListener('yap:set-view', handleSetView);
            window.removeEventListener('yap:turntable', handleTurntable);
            cancelAnimationFrame(raf);
        };
    }, [camera, controls, invalidate]);

    return null;
}

/**
 * Zoom chord handler: listens to the shared 'yap:zoom-to-fit' /
 * 'yap:zoom-to-selection' / 'yap:zoom-out' events dispatched by the
 * canonical commands (ZOOM/ZE/ZA/ZZ, 3D _ZOOMEXTENTS...) and the type
 * buffer. Mounted in EVERY workbench canvas — previously only the BIM
 * viewport listened, so these commands were silent no-ops in CAD/3D.
 * Works for both camera projections; selection extents resolve through the
 * shared document ledger so CAD entities and BIM elements are framed alike.
 */
function ZoomChordHandler() {
    const camera = useThree((s) => s.camera);
    const gl = useThree((s) => s.gl);
    const controls = useThree((s) => s.controls) as
        | { target: THREE.Vector3; update: () => void }
        | null;
    const invalidate = useThree((s) => s.invalidate);

    useEffect(() => {
        // Shared document extent (CAD entities + BIM elements) — the same
        // framing source the quad preview cells use (rendering/documentBounds).
        const collectBounds = collectDocumentBounds;

        const frame = (box: THREE.Box3 | null, keepDirection: boolean) => {
            const center = box ? box.getCenter(new THREE.Vector3()) : new THREE.Vector3();
            const radius = box
                ? Math.max(box.getSize(new THREE.Vector3()).length() / 2, 0.5)
                : 10;
            if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
                const ortho = camera as THREE.OrthographicCamera;
                const viewH = Math.max(gl.domElement.clientHeight, 1);
                const viewW = Math.max(gl.domElement.clientWidth, 1);
                // world height at zoom 1 for drei's pixel-frustum cameras
                // equals the canvas pixel size, so zoom = px / world extent.
                ortho.zoom = Math.max(Math.min(viewW, viewH) / (radius * 2.4), 0.01);
                ortho.updateProjectionMatrix();
            } else {
                const fov = (camera as THREE.PerspectiveCamera).fov ?? 50;
                const distance = radius / Math.sin(THREE.MathUtils.degToRad(fov) / 2);
                const direction = keepDirection && controls
                    ? new THREE.Vector3().subVectors(camera.position, controls.target).normalize()
                    : new THREE.Vector3(1, 1, 1).normalize();
                camera.position.copy(center).add(direction.multiplyScalar(distance));
            }
            if (controls) {
                controls.target.copy(center);
                controls.update();
            } else {
                camera.lookAt(center);
            }
            camera.updateProjectionMatrix();
            invalidate();
            notifyCameraChanged();
        };

        const zoomToFit = () => frame(collectBounds(null), false);
        // ZOOM Previous: a small view-state stack. Every framing action
        // captures the state before it runs so Z/P walks back through them.
        type ViewSnap = { pos: THREE.Vector3; target: THREE.Vector3; zoom: number };
        const history: ViewSnap[] = [];
        const snapshot = (): ViewSnap | null => {
            if (!camera) return null;
            return {
                pos: camera.position.clone(),
                target: controls ? controls.target.clone() : new THREE.Vector3(),
                zoom: (camera as THREE.OrthographicCamera).isOrthographicCamera
                    ? (camera as THREE.OrthographicCamera).zoom : 1,
            };
        };
        const frameAndRecord = (box: THREE.Box3 | null, keepDirection: boolean): void => {
            const before = snapshot();
            frame(box, keepDirection);
            if (before) {
                history.push(before);
                if (history.length > 20) history.shift();
            }
        };
        const zoomPrevious = () => {
            const prev = history.pop();
            if (!prev) { frame(collectBounds(null), false); return; }
            camera.position.copy(prev.pos);
            const ortho = camera as THREE.OrthographicCamera;
            if (ortho.isOrthographicCamera) {
                ortho.zoom = prev.zoom;
                ortho.updateProjectionMatrix();
            }
            if (controls) {
                controls.target.copy(prev.target);
                controls.update();
            } else {
                camera.lookAt(prev.target);
            }
            invalidate();
            notifyCameraChanged();
        };
        // ZOOM Window: two world corners define the frame box.
        const zoomRegion = (e: Event) => {
            const detail = (e as CustomEvent<{ a: number[]; b: number[] }>).detail;
            if (!detail?.a || !detail?.b) return;
            const pa = new THREE.Vector3(detail.a[0], detail.a[1], 0);
            const pb = new THREE.Vector3(detail.b[0], detail.b[1], 0);
            const box = new THREE.Box3().setFromPoints([pa, pb]);
            // Give the flat 2D window a minimum depth so the fit is sane.
            if (box.min.z === box.max.z) box.min.z -= 1, box.max.z += 1;
            frameAndRecord(box, false);
        };
        // ZOOM Scale: n (relative) or nXP (paper units) — ortho zoom only.
        const zoomScale = (e: Event) => {
            const factor = (e as CustomEvent<{ factor: number }>).detail?.factor;
            if (!factor || !Number.isFinite(factor) || factor <= 0) return;
            const before = snapshot();
            const ortho = camera as THREE.OrthographicCamera;
            if (ortho.isOrthographicCamera) {
                ortho.zoom = Math.max(0.01, ortho.zoom * factor);
                ortho.updateProjectionMatrix();
            } else {
                const center = controls ? controls.target.clone() : new THREE.Vector3();
                const dir = new THREE.Vector3().subVectors(camera.position, center);
                dir.multiplyScalar(1 / factor);
                camera.position.copy(center).add(dir);
            }
            if (before) history.push(before);
            invalidate();
            notifyCameraChanged();
        };
        // PAN command vector: translate camera + target together on screen.
        const panBy = (e: Event) => {
            const d = (e as CustomEvent<{ dx: number; dy: number }>).detail;
            if (!d) return;
            const before = snapshot();
            camera.position.x += d.dx;
            camera.position.y += d.dy;
            if (controls) {
                controls.target.x += d.dx;
                controls.target.y += d.dy;
                controls.update();
            }
            if (before) history.push(before);
            invalidate();
            notifyCameraChanged();
        };
        const zoomToSelection = () => {
            const ids = new Set([
                ...useAppStore.getState().selectedIds,
                ...useBIMStore.getState().selection,
            ]);
            frameAndRecord(collectBounds(ids.size ? Array.from(ids) : null), true);
        };
        const zoomOut = () => {
            const center = controls ? controls.target.clone() : new THREE.Vector3();
            const direction = new THREE.Vector3().subVectors(camera.position, center).normalize();
            camera.position.add(direction.multiplyScalar(camera.position.distanceTo(center) * 0.5));
            if (controls) controls.update();
            camera.updateProjectionMatrix();
            invalidate();
            notifyCameraChanged();
        };

        window.addEventListener('yap:zoom-to-fit', zoomToFit);
        window.addEventListener('yap:zoom-to-selection', zoomToSelection);
        window.addEventListener('yap:zoom-out', zoomOut);
        window.addEventListener('yap:zoom-previous', zoomPrevious);
        window.addEventListener('yap:zoom-region', zoomRegion);
        window.addEventListener('yap:zoom-scale', zoomScale);
        window.addEventListener('yap:pan-by', panBy);
        return () => {
            window.removeEventListener('yap:zoom-to-fit', zoomToFit);
            window.removeEventListener('yap:zoom-to-selection', zoomToSelection);
            window.removeEventListener('yap:zoom-out', zoomOut);
            window.removeEventListener('yap:zoom-previous', zoomPrevious);
            window.removeEventListener('yap:zoom-region', zoomRegion);
            window.removeEventListener('yap:zoom-scale', zoomScale);
            window.removeEventListener('yap:pan-by', panBy);
        };
    }, [camera, controls, gl, invalidate]);

    return null;
}

export function EngineViewport({
    camera,
    children,
    lighting,
    environmentOverlays,
    grid,
    axes,
    postProcessing = false,
    shadows = true,
    controls,
    onContextMenu,
    containerClassName,
    // Mode-aware fallback (light gray / dark surface) — never a pinned dark
    // hex, or the container glows dark behind the canvas in light mode.
    containerBg = 'bg.canvas',
    canvasClassName,
    suspenseFallback,
    overlay,
    showViewCube = true,
    environment = true,
    viewCommands = true,
    middleButtonMode = 'pan',
}: EngineViewportProps) {
    const renderSettings = useRenderSettingsStore((s) => s.settings);
    const controlsRef = useRef<any>(null);

    // Alt + Middle Click = orbit, released = pan (shared navigation model).
    // MBUTTONPAN parity: 1 = middle-drag pans (default), 0 = the middle
    // button opens the context (shortcut) menu and pan is disabled.
    // 'orbit' mode (Sketch): the middle button always orbits regardless of
    // MBUTTONPAN, and Shift+Middle pans (Sketch navigation language).
    useEffect(() => {
        const middleButtons = (): THREE.MOUSE => {
            if (middleButtonMode === 'orbit') return THREE.MOUSE.ROTATE;
            return useSystemVarsStore.getState().MBUTTONPAN === 0 ? THREE.MOUSE.ROTATE : THREE.MOUSE.PAN;
        };
        const applyMiddle = () => {
            if (!controlsRef.current) return;
            // With the shortcut-menu mode on, freeze middle to ROTATE only
            // for Alt+middle below; the plain gesture is intercepted instead.
            controlsRef.current.mouseButtons.MIDDLE = middleButtons();
            controlsRef.current.update();
        };
        applyMiddle();
        const unsub = useSystemVarsStore.subscribe(applyMiddle);
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!controlsRef.current) return;
            if (e.key === 'Alt') {
                controlsRef.current.mouseButtons.MIDDLE = THREE.MOUSE.ROTATE;
                controlsRef.current.update();
            } else if (e.key === 'Shift' && middleButtonMode === 'orbit') {
                // Sketch pan gesture: Shift temporarily hands the middle
                // button to panning while the orbit key is held.
                controlsRef.current.mouseButtons.MIDDLE = THREE.MOUSE.PAN;
                controlsRef.current.update();
            }
        };
        const handleKeyUp = (e: KeyboardEvent) => {
            if (!controlsRef.current) return;
            if (e.key === 'Alt' || (e.key === 'Shift' && middleButtonMode === 'orbit')) {
                controlsRef.current.mouseButtons.MIDDLE = middleButtons();
                controlsRef.current.update();
            }
        };
        // MBUTTONPAN parity: kill the browser's native middle-button
        // autoscroll on the canvas so the drag reaches OrbitControls as a
        // clean pan gesture (pointerdown already fired before mousedown, so
        // this only suppresses the default action, never the control itself).
        const handleMouseDown = (e: MouseEvent) => {
            if (e.button === 1 && (e.target as HTMLElement)?.tagName === 'CANVAS') {
                e.preventDefault();
                // MBUTTONPAN=0: middle click opens the shortcut menu (the
                // global context menu), mirroring 2D's behavior.
                if (useSystemVarsStore.getState().MBUTTONPAN === 0) {
                    useContextMenuStore.getState().show(e.clientX, e.clientY, undefined, useAppStore.getState().selectedIds);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('mousedown', handleMouseDown);
        return () => {
            unsub();
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('mousedown', handleMouseDown);
        };
    }, [middleButtonMode]);

    const canvas = (
        <Canvas
            gl={{ ...ENGINE_GL_CONFIG }}
            events={overlaySafeEvents}
            shadows={shadows}
            dpr={renderSettings.dpr}
            frameloop="demand"
            className={canvasClassName}
        >
            {/* Demand render loop controller */}
            <DemandRenderController />

            {/* Repaint the frame when the suite color mode flips */}
            <ThemeRepaintMount />

            {/* Repaint the frame when Settings › Color rewrites the viewport tokens */}
            <ViewportColorsRepaintMount />

            {/* MCP screenshot capture registration */}
            <ViewportCaptureMount />

            {/* Camera registry for DOM overlays (axes gizmo) */}
            <EngineCameraRegistryMount />

            {/* Camera (pose owned by the workbench shell) */}
            {camera}

            {/* Navigation */}
            <OrbitControls
                key={controls?.controlsKey}
                ref={controlsRef}
                makeDefault
                enableDamping={false}
                target={controls?.target}
                enableRotate={controls?.enableRotate ?? true}
                enableZoom={controls?.enableZoom ?? true}
                enablePan={controls?.enablePan ?? true}
                minPolarAngle={controls?.minPolarAngle ?? 0}
                maxPolarAngle={controls?.maxPolarAngle ?? Math.PI}
                minDistance={controls?.minDistance ?? 1}
                maxDistance={controls?.maxDistance ?? 1000}
                screenSpacePanning={controls?.screenSpacePanning ?? false}
                onChange={() => { notifyCameraChanged(); }}
                mouseButtons={controls?.mouseButtons ?? {
                    LEFT: undefined,
                    MIDDLE: THREE.MOUSE.PAN,
                    RIGHT: undefined,
                }}
            />

            {/* Lighting rig (workbench-provided) */}
            {lighting}

            {/* Procedural PBR environment (offline-safe, no network HDR).
                2D drafting shells disable it: nothing lit samples the env. */}
            {environment && <ProceduralEnvironment />}

            {/* Settings-driven post effects (3D workbench only) */}
            {postProcessing && <PostProcessing settings={renderSettings} />}

            {grid}
            {axes}

            {/* ViewCube-style Gizmo */}
            <ViewCubeContainer enabled={showViewCube} />

            {/* Standard orientation commands (Top/Front/Right/Perspective).
                Off for fixed-plan 2D shells: they must never tilt the camera. */}
            {viewCommands && <ViewCommandHandler />}

            {/* Shared zoom chords (fit / selection / out) for all workbenches */}
            <ZoomChordHandler />

            {/* Environment-attached overlays (sun path, crop regions, ...) */}
            {environmentOverlays}

            {/* Scene content */}
            {children}
        </Canvas>
    );

    return (
        <Box
            className={containerClassName}
            position="relative"
            w="100%"
            h="100%"
            bg={containerBg ?? undefined}
            onContextMenu={onContextMenu}
        >
            {suspenseFallback ? (
                <Suspense fallback={suspenseFallback}>{canvas}</Suspense>
            ) : (
                canvas
            )}
            {overlay}
        </Box>
    );
}

/**
 * Default context-menu handler shared by workbench shells: opens the global
 * context menu with the current selection at the pointer. When the persisted
 * "right-click completes command" preference is active and a command is
 * running, a plain right-click finishes it instead (Shift = show the menu).
 */
export function useViewportContextMenu() {
    const selectedIds = useAppStore((s) => s.selectedIds);
    const contextMenu = useContextMenuStore((s) => s.show);
    return useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (shouldRightClickComplete(e.shiftKey)) {
            finishActiveCommand();
            return;
        }
        // Idle + plain right-click: re-arm the last-used tool instead of the menu.
        if (tryRepeatLastCommand(e.shiftKey)) return;
        contextMenu(e.clientX, e.clientY, undefined, selectedIds);
    }, [contextMenu, selectedIds]);
}

/* Re-exported so shells can build standard grids without importing drei. */
export { Grid };
